/** Public @handle validation — shared by client and server. */

export const HANDLE_MIN = 3;
export const HANDLE_MAX = 30;

export const RESERVED_HANDLES = new Set([
  'admin',
  'administrator',
  'api',
  'settings',
  'billing',
  'login',
  'logout',
  'signup',
  'register',
  'institution',
  'institutions',
  'explore',
  'network',
  'messages',
  'people',
  'profile',
  'circles',
  'circle',
  'dashboard',
  'support',
  'help',
  'ertzyx',
  'pulse',
  'feed',
  'community',
  'vault',
  'proof',
  'trust',
  'auth',
  'www',
  'app',
  'static',
  'public',
  'private',
  'null',
  'undefined',
  'me',
  'you',
  'about',
  'contact',
  'pricing',
  'terms',
  'privacy',
]);

const HANDLE_PATTERN = /^[a-z0-9][a-z0-9._]{1,28}[a-z0-9]$|^[a-z0-9]{3}$/;

export type HandleValidation = { ok: true; handle: string } | { ok: false; error: string };

export function normalizePublicHandle(raw: string): string {
  return raw
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/^@+/, '')
    .replace(/[\s-]+/g, '.')
    .replace(/[^a-z0-9._]/g, '')
    .replace(/[._]{2,}/g, '.')
    .replace(/^[._]+|[._]+$/g, '');
}

export function validatePublicHandle(raw: string): HandleValidation {
  const handle = normalizePublicHandle(raw);
  if (!handle) return { ok: false, error: 'Handle is required.' };
  if (handle.length < HANDLE_MIN) {
    return { ok: false, error: `Handle must be at least ${HANDLE_MIN} characters.` };
  }
  if (handle.length > HANDLE_MAX) {
    return { ok: false, error: `Handle must be at most ${HANDLE_MAX} characters.` };
  }
  if (!HANDLE_PATTERN.test(handle)) {
    return {
      ok: false,
      error:
        'Use lowercase letters, numbers, dots, and underscores. Must start and end with a letter or number.',
    };
  }
  if (handle.includes('..')) return { ok: false, error: 'Handle cannot contain consecutive dots.' };
  if (RESERVED_HANDLES.has(handle)) {
    return { ok: false, error: 'This handle is reserved. Choose another.' };
  }
  return { ok: true, handle };
}

/**
 * Suggests a human-readable handle candidate from a display name (e.g. a person's full name or
 * an institution name). Uses the same character set the canonical validator accepts (lowercase
 * letters, numbers, dots, underscores) — never hyphens, so the suggestion is always immediately
 * valid input for validatePublicHandle. Words are joined with a dot for readability (e.g. "Jane
 * Smith" -> "jane.smith"). This only proposes a starting point; the caller must still check
 * availability and save through a canonical server route like any other handle change.
 */
export function suggestPublicHandleFromName(name: string, fallbackSeed?: string): string {
  const words = normalizedNameWords(name);

  let candidate = words.join('.').replace(/\.{2,}/g, '.');

  if (!candidate && fallbackSeed) {
    candidate = fallbackSeed
      .toLowerCase()
      .replace(/[^a-z0-9._]/g, '')
      .replace(/\.{2,}/g, '.');
  }
  // Not "member"-adjacent reserved words — "institution"/"institutions" are themselves reserved,
  // so a naive fallback risks colliding with validatePublicHandle's own reserved-word rejection.
  if (!candidate) candidate = 'member';

  candidate = candidate
    .slice(0, HANDLE_MAX)
    .replace(/^[._]+/, '')
    .replace(/[._]+$/, '');

  if (candidate.length < HANDLE_MIN) {
    const filler = 'member'.slice(0, HANDLE_MIN - candidate.length + 1);
    candidate = `${candidate}${candidate ? '.' : ''}${filler}`.slice(0, HANDLE_MAX);
  }

  return candidate;
}

function normalizedNameWords(name: string): string[] {
  return (name || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

/** Ordered, readable candidates used by every application-side individual allocator. */
export function individualHandleCandidates(name: string): string[] {
  const words = normalizedNameWords(name);
  const primary = suggestPublicHandleFromName(name);
  const candidates = [primary];

  if (words.length > 1) {
    candidates.push(`${words[0]}.${words.at(-1)?.slice(0, 1)}`);
  }

  const validCandidates = [...new Set(candidates)].filter(
    (candidate) => validatePublicHandle(candidate).ok
  );
  return validCandidates.length > 0 ? validCandidates : ['member'];
}

const MACHINE_GENERATED_HANDLE_PATTERN = /(?:^|[._-])[0-9a-f]{12,16}(?:[._-]\d+)?$/i;
const UUID_HANDLE_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;

/** Detects legacy UUID/hex allocator output. It never mutates the stored handle. */
export function isLikelyMachineGeneratedHandle(handle: string | null | undefined): boolean {
  const value = (handle || '').trim().replace(/^@+/, '');
  return MACHINE_GENERATED_HANDLE_PATTERN.test(value) || UUID_HANDLE_PATTERN.test(value);
}

/**
 * The single canonical source for what to show a human as "the username" — every read-only
 * @handle surface (sidebar, public profile, people search, preview cards) should render this
 * instead of the raw stored column. Returns null for a missing or machine-generated legacy
 * handle so callers fall back to a clean identity element (vault ID, initials, etc.) instead of
 * a hex/UUID suffix. Never use this for routing or for prefilling an editable handle input —
 * /u/[handle] URLs and the settings form must keep reading the raw, unmodified public_handle so
 * existing profile links and in-progress edits are never silently rewritten.
 */
export function displayPublicHandle(handle: string | null | undefined): string | null {
  const value = (handle || '').trim().replace(/^@+/, '');
  if (!value || isLikelyMachineGeneratedHandle(value)) return null;
  return value;
}

/**
 * Resolves a clean, human-readable, guaranteed-available handle for a brand-new account, given a
 * seed name (e.g. the display name entered at registration) and a caller-supplied availability
 * checker (kept DB-agnostic here so this stays unit-testable without a real database). Starts
 * from suggestPublicHandleFromName's base candidate; on conflict, appends a plain numeric suffix
 * (2, 3, 4, ...) rather than a random tail, so the result stays readable and shareable. Never
 * calls the checker for a candidate that would fail validatePublicHandle. Only ever used for a
 * handle that is about to be set for the FIRST time — this does not rewrite an existing handle.
 */
export async function resolveAvailableHandle(
  seedName: string,
  isAvailable: (candidate: string) => Promise<boolean>,
  options?: { maxAttempts?: number }
): Promise<string> {
  const readableCandidates = individualHandleCandidates(seedName);
  const base = readableCandidates[0] || 'member';
  const maxAttempts = options?.maxAttempts ?? 999;

  for (const candidate of readableCandidates) {
    const validation = validatePublicHandle(candidate);
    if (validation.ok && (await isAvailable(validation.handle))) return validation.handle;
  }

  for (let attempt = 2; attempt <= maxAttempts + 1; attempt += 1) {
    const suffix = String(attempt);
    const trimmedBase = base
      .slice(0, Math.max(HANDLE_MIN, HANDLE_MAX - suffix.length))
      .replace(/[._]+$/, '');
    const candidate = `${trimmedBase}${suffix}`;
    const validation = validatePublicHandle(candidate);
    if (!validation.ok) continue;
    if (await isAvailable(validation.handle)) return validation.handle;
  }

  // Fail closed after a bounded numeric search. Never leak time, UUID, or auth-ID material.
  throw new Error('No readable public handle is available for this name.');
}

export function profilePathForUser(profile: {
  public_handle?: string | null;
  vault_id?: string | null;
  role?: string | null;
  id?: string;
}): string | null {
  if (profile.public_handle) return `/u/${profile.public_handle}`;
  if (profile.vault_id) return `/u/${profile.vault_id}`;
  return null;
}

const DEFAULT_SITE_HOST = 'ledger.ertzyx.com';

/** Absolute public profile URL (handle preferred, ERV fallback). */
export function publicProfileAbsoluteUrl(
  profile: { public_handle?: string | null; vault_id?: string | null },
  host = DEFAULT_SITE_HOST
): string {
  const path = profilePathForUser(profile);
  return `https://${host}${path || '/u/ERV-0000-0000'}`;
}

export function publicProfileHostPath(
  profile: { public_handle?: string | null; vault_id?: string | null },
  host = DEFAULT_SITE_HOST
): string {
  const path = profilePathForUser(profile);
  return `${host}${path || '/u/ERV-0000-0000'}`;
}
